import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Plus, Search, Undo2 } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fetchCalculationNeighbors, fetchCalculations } from '../api/calculations.api'
import { loadListContext, loadRecentCalculations, rememberRecentCalculation, type RecentCalculation } from '../lib/kalkulation-navigation'
import type { CalculationNeighbors } from '../types/calculation.types'
import { CalculationCreateDialog } from './CalculationCreateDialog'

/**
 * AP-37 9. — **der Navigationsrahmen rechts im Kopf** (Muster AP-35 Nachtrag 5):
 * Suchfeld (leer: zuletzt geöffnet; getippt: Nummer, Bezeichnung, Kunde),
 * Blättern ‹ › in der Reihenfolge der Liste mit ihren letzten Filtern, ↶ zur
 * zuletzt geöffneten Kalkulation, „Neu". Tasten: `/` sucht, `[` und `]` blättern.
 */
export function CalculationNavigator({ kalkulation }: { kalkulation: RecentCalculation & { isTemplate: boolean } }) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const darfAnlegen = erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const [recent, setRecent] = useState<RecentCalculation[]>(loadRecentCalculations)
  const [nachbarn, setNachbarn] = useState<CalculationNeighbors | null>(null)
  const [anlegen, setAnlegen] = useState(false)
  const { id, label, name, isTemplate } = kalkulation

  useEffect(() => {
    setRecent(rememberRecentCalculation({ id, label, name }))
  }, [id, label, name])
  const zuletzt = recent.find((p) => p.id !== id) ?? null

  useEffect(() => {
    setNachbarn(null)
    let aktiv = true
    fetchCalculationNeighbors(id, { ...loadListContext(user?.id), templates: isTemplate })
      .then((n) => aktiv && setNachbarn(n))
      .catch(() => aktiv && setNachbarn(null))
    return () => {
      aktiv = false
    }
  }, [id, isTemplate, user?.id])

  const oeffne = useCallback((ziel: string) => navigate(`/calculations/${ziel}`), [navigate])
  const zurueck = useCallback(() => nachbarn?.previousId && oeffne(nachbarn.previousId), [nachbarn, oeffne])
  const weiter = useCallback(() => nachbarn?.nextId && oeffne(nachbarn.nextId), [nachbarn, oeffne])

  const eingabe = useRef<HTMLInputElement>(null)
  const rahmen = useRef<HTMLDivElement>(null)
  const [suche, setSuche] = useState('')
  const [offen, setOffen] = useState(false)
  const [treffer, setTreffer] = useState<(RecentCalculation & { kunde?: string | null })[] | null>(null)
  const [markiert, setMarkiert] = useState(0)

  useEffect(() => {
    const q = suche.trim()
    if (!q) return setTreffer(null)
    let aktiv = true
    const t = setTimeout(() => {
      fetchCalculations({ search: q, limit: 8, templates: isTemplate })
        .then((r) => aktiv && setTreffer(r.data.map((k) => ({ id: k.id, label: k.label, name: k.name, kunde: k.client?.name ?? k.prospectName }))))
        .catch(() => aktiv && setTreffer([]))
    }, 200)
    return () => {
      aktiv = false
      clearTimeout(t)
    }
  }, [suche, isTemplate])

  const eintraege = useMemo(
    () => (suche.trim() ? (treffer ?? []) : recent.filter((p) => p.id !== id).slice(0, 8)),
    [suche, treffer, recent, id],
  )
  useEffect(() => setMarkiert(0), [eintraege])

  const schliessen = () => {
    setOffen(false)
    setSuche('')
    eingabe.current?.blur()
  }
  const waehle = (ziel: string) => {
    schliessen()
    oeffne(ziel)
  }

  useEffect(() => {
    if (!offen) return
    const weg = (e: MouseEvent) => {
      if (rahmen.current && !rahmen.current.contains(e.target as Node)) {
        setOffen(false)
        setSuche('')
      }
    }
    document.addEventListener('mousedown', weg)
    return () => document.removeEventListener('mousedown', weg)
  }, [offen])

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const ziel = e.target as HTMLElement | null
      if (ziel && (ziel.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(ziel.tagName))) return
      if (e.key === '/') {
        e.preventDefault()
        eingabe.current?.focus()
      } else if (e.key === '[') zurueck()
      else if (e.key === ']') weiter()
    }
    document.addEventListener('keydown', taste)
    return () => document.removeEventListener('keydown', taste)
  }, [zurueck, weiter])

  const tastenImFeld = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOffen(true)
      setMarkiert((m) => Math.min(m + 1, eintraege.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setMarkiert((m) => Math.max(m - 1, 0))
    } else if (e.key === 'Enter') {
      const p = eintraege[markiert]
      if (p) waehle(p.id)
    } else if (e.key === 'Escape') schliessen()
  }

  const knopfKlein =
    'inline-flex h-[26px] w-[26px] items-center justify-center border border-border bg-background text-foreground hover:bg-muted disabled:cursor-default disabled:opacity-40'

  return (
    <div ref={rahmen} className="relative w-full space-y-1 rounded-md border border-border bg-background p-1 sm:w-[340px]">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={eingabe}
          type="text"
          value={suche}
          onChange={(e) => {
            setSuche(e.target.value)
            setOffen(true)
          }}
          onFocus={() => setOffen(true)}
          onKeyDown={tastenImFeld}
          placeholder={isTemplate ? 'Vorlage suchen …' : 'Kalkulation suchen …'}
          aria-label="Kalkulation suchen"
          className="h-[26px] w-full rounded-md border border-input bg-background pl-8 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border px-1 text-[10px] text-muted-foreground">/</kbd>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="inline-flex">
          <button type="button" className={`${knopfKlein} rounded-l-md`} onClick={zurueck} disabled={!nachbarn?.previousId} aria-label="Zurück blättern" title="Zurück  [">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" className={`${knopfKlein} -ml-px rounded-r-md`} onClick={weiter} disabled={!nachbarn?.nextId} aria-label="Weiter blättern" title="Weiter  ]">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          className={`${knopfKlein} rounded-md`}
          onClick={() => zuletzt && oeffne(zuletzt.id)}
          disabled={!zuletzt}
          title={zuletzt ? `Zurück zu ${zuletzt.label} ${zuletzt.name}` : 'Noch nichts anderes geöffnet'}
          aria-label="Zuletzt geöffnet"
        >
          <Undo2 className="h-4 w-4" />
        </button>
        {nachbarn?.position && nachbarn.total > 1 && (
          <span className="text-xs text-muted-foreground" title="Stelle in der gefilterten Liste">
            {nachbarn.position}/{nachbarn.total}
          </span>
        )}
        <span className="flex-1" />
        {darfAnlegen && (
          <button type="button" className="inline-flex h-[26px] items-center gap-1 rounded-md border border-border px-2.5 text-sm" onClick={() => setAnlegen(true)}>
            <Plus className="h-4 w-4" /> Neu
          </button>
        )}
      </div>

      {offen && (
        <div className="absolute left-0 right-0 top-[calc(26px+0.5rem)] z-50 rounded-md border border-border p-1 shadow-lg" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
          <p className="px-2 pb-1 pt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            {suche.trim() ? (treffer === null ? 'Sucht …' : `${eintraege.length} Treffer`) : 'Zuletzt geöffnet'}
          </p>
          {eintraege.length === 0 && (treffer !== null || !suche.trim()) && (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">{suche.trim() ? 'Nichts gefunden' : 'Noch nichts anderes geöffnet'}</p>
          )}
          {eintraege.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setMarkiert(i)}
              onClick={() => waehle(p.id)}
              className={`flex w-full items-baseline justify-between gap-3 rounded px-2 py-1.5 text-left text-sm ${i === markiert ? 'bg-accent text-accent-foreground' : ''}`}
            >
              <span className="min-w-0 truncate">
                <span className="text-muted-foreground">{p.label}</span> {p.name}
              </span>
              {(p as { kunde?: string | null }).kunde && (
                <span className="shrink-0 truncate text-xs text-muted-foreground">{(p as { kunde?: string | null }).kunde}</span>
              )}
            </button>
          ))}
          <div className="mt-1 flex items-center justify-between border-t border-border px-2 pt-1.5 text-xs text-muted-foreground">
            <span>↑↓ wählen · Enter öffnen · Esc schließen</span>
            <Link to={isTemplate ? '/calculations/templates' : '/calculations'} className="text-primary hover:underline" onClick={() => setOffen(false)}>
              Liste →
            </Link>
          </div>
        </div>
      )}

      {anlegen && (
        <CalculationCreateDialog
          vorlage={isTemplate}
          onClose={() => setAnlegen(false)}
          onSaved={(k) => {
            setAnlegen(false)
            navigate(`/calculations/${k.id}`)
          }}
        />
      )}
    </div>
  )
}
